import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FormProvider, useForm } from 'react-hook-form';

import {
  getCourseEnrollmentCriteriaOptions,
  getCourseEnrollmentCriteriaQueryKey,
  updateCourseEnrollmentCriteriaMutation,
} from '@drivingdoc/api-client/generated/react-query';
import { type MinimalUser } from 'api';

import CheckBoxInput from 'components/FormInputs/CheckBoxInput';
import IntervalInput from 'components/FormInputs/IntervalInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';
import normalizePgInterval from 'utils/normalizePgInterval.ts';

import DateModified from '../../components/DateModified.tsx';
import DialogFormSection from '../../components/DialogFormSection.tsx';
import FeedbackDialog from '../../FeedbackDialog';
import { FormInputValues, FormOutputValues, FormSchema } from './formSchema.ts';

dayjs.extend(calendar);

interface UpdateCourseEnrollmentCriteriaDialogPayload {
  /**
   * The course id to update
   */
  courseId: string;
}

export function UpdateCourseEnrollmentCriteriaDialog({
  payload,
  open,
  onClose,
}: DialogProps<UpdateCourseEnrollmentCriteriaDialogPayload>) {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  const updateCourseEnrollmentCriteria = useMutation({
    ...updateCourseEnrollmentCriteriaMutation(),
  });

  const courseEnrollmentCriteria = useQuery({
    ...getCourseEnrollmentCriteriaOptions({
      path: {
        id: payload.courseId,
      },
    }),
    enabled: !!payload.courseId,
  });

  const form = useForm<FormInputValues, unknown, FormOutputValues>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    values: {
      min_age_to_register: normalizePgInterval(courseEnrollmentCriteria.data?.min_age_to_register),
      max_age_to_register: normalizePgInterval(courseEnrollmentCriteria.data?.max_age_to_register),
      min_age_at_first_period: normalizePgInterval(courseEnrollmentCriteria.data?.min_age_at_first_period),
      max_age_at_first_period: normalizePgInterval(courseEnrollmentCriteria.data?.max_age_at_first_period),
      require_permit_to_register: courseEnrollmentCriteria.data?.require_permit_to_register ?? false,
    },
  });

  const { handleSubmit } = form;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (data: FormOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('This will only affect new registrations.', {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateCourseEnrollmentCriteria.mutateAsync({
        path: { id: payload.courseId },
        body: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: getCourseEnrollmentCriteriaQueryKey({
            path: { id: payload.courseId },
          }),
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Requirements Updated',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Update Course Enrollment Requirements</DialogTitle>

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogContent>
            <DialogFormSection title="Age Restrictions">
              <Grid size={12}>
                <IntervalInput name="min_age_to_register" label="Minimum Age To Enroll" years months days />
              </Grid>
              <Grid size={12}>
                <IntervalInput name="max_age_to_register" label="Maximum Age To Enroll" years months days />
              </Grid>
              <Grid size={12}>
                <IntervalInput name="min_age_at_first_period" label="Minimum Age At First Period" years months days />
              </Grid>
              <Grid size={12}>
                <IntervalInput name="max_age_at_first_period" label="Maximum Age At First Period" years months days />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Permit Requirements">
              <Grid size={12}>
                <CheckBoxInput name="require_permit_to_register" label="Require Permit To Enroll" />
              </Grid>
            </DialogFormSection>

            <Box pt={2}>
              <DateModified
                prefix="Updated: "
                date={courseEnrollmentCriteria.data?.updated_at}
                user={courseEnrollmentCriteria.data?.updated_by_user as MinimalUser}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Update</Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={updateCourseEnrollmentCriteria.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCourseEnrollmentCriteriaDialog;
